import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doctorAPI } from '../../../utils/api';
import { useNotification } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';
import { handleIOSPayPerUse } from '../../../utils/iapPayment';
import appleIAP, { PRODUCT_IDS } from '../../../services/appleIAP';

const GetVerified = () => {
  const [applePrice, setApplePrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iapFinished, setIapFinished] = useState(false);
  const { user, refreshUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    initIAP();
  }, []);

  const getBlueTickProductId = () => {
    const role = user?.role || 'doctor';
    const blueTickMap = {
      doctor: PRODUCT_IDS.doctor.blueTick,
      retailer: PRODUCT_IDS.retailer.blueTick,
      clinic: PRODUCT_IDS.clinic.blueTick,
      hospital: PRODUCT_IDS.hospital.blueTick,
    };
    return blueTickMap[role] || PRODUCT_IDS.doctor.blueTick;
  };

  const initIAP = async () => {
    try {
      await appleIAP.initialize();
      
      const loadPrice = () => {
        const productId = getBlueTickProductId();
        const p = appleIAP.getProduct(productId);
        if (p) setApplePrice(p.localizedPrice);
      };
      
      loadPrice();
      const removeListener = appleIAP.on('products-updated', loadPrice);
      
      let attempts = 0;
      const pollInterval = setInterval(() => {
        attempts++;
        const hasPrice = appleIAP.getProduct(getBlueTickProductId())?.localizedPrice;
        if (hasPrice || attempts >= 10) {
          clearInterval(pollInterval);
          loadPrice();
          setIapFinished(true);
          removeListener?.();
          setLoading(false);
        } else {
          loadPrice();
        }
      }, 1000);

      return () => {
        clearInterval(pollInterval);
        removeListener?.();
      };
    } catch (err) {
      console.error('IAP init error:', err);
      setIapFinished(true);
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    try {
      if (!applePrice) {
        showError('Price is currently unavailable. Please try again in a moment.');
        return;
      }

      await handleIOSPayPerUse({
        productId: getBlueTickProductId(),
        onSuccess: async () => {
          try {
            await doctorAPI.requestBlueTick('apple_iap');
            showSuccess('Verification request submitted successfully!');
            if (refreshUser) await refreshUser();
            navigate(`/${user?.role || 'doctor'}/dashboard`);
          } catch (error) {
            showError('Failed to submit verification request');
          }
        },
        onError: (msg) => showError(msg),
      });
    } catch (error) {
      showError('Payment initialization failed');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-dark">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-dark flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-900/80 border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden backdrop-blur-xl">
        <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 blur-3xl rounded-full -mr-10 -mt-10"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-yellow-500/10 blur-3xl rounded-full -ml-10 -mb-10"></div>

        <div className="relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-blue-500/10 border border-blue-500/30 mb-8 relative shadow-[0_0_30px_rgba(59,130,246,0.15)]">
            <div className="absolute inset-0 rounded-full border border-blue-400/20 animate-[spin_4s_linear_infinite]"></div>
            <svg className="w-12 h-12 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>

          <h1 className="text-4xl font-extrabold text-white mb-4 tracking-tight">Get Verified</h1>
          <p className="text-gray-400 mb-8 leading-relaxed">
            Build trust with users by adding a blue tick to your profile. Verified specialists get <strong className="text-blue-400 font-bold">3x more appointments</strong>.
          </p>

          <div className="bg-white/5 rounded-2xl p-6 mb-8 border border-white/5 shadow-inner">
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-300 font-medium uppercase tracking-wide text-sm">Verification Fee</span>
              <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-600">
                {applePrice || (iapFinished ? 'Unavailable' : 'Loading...')}
              </span>
            </div>
            <p className="text-xs text-start text-gray-500 font-medium">One-time payment for lifetime verification badge. App Store Subscription.</p>
          </div>

          <button
            onClick={handlePurchase}
            disabled={!applePrice && !iapFinished}
            className={`w-full font-bold py-4 rounded-xl shadow-lg transition-all duration-300 text-lg ${
              !applePrice && !iapFinished
                ? 'bg-white/5 text-gray-500 cursor-wait border border-white/10'
                : !applePrice && iapFinished
                  ? 'bg-yellow-400/10 text-yellow-400 cursor-not-allowed border border-yellow-400/20'
                  : 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(59,130,246,0.6)]'
            }`}
          >
            {!applePrice && !iapFinished ? 'Loading Price...' : !applePrice && iapFinished ? 'Price Unavailable' : 'Purchase & Verify'}
          </button>

          <button
            onClick={() => navigate(-1)}
            className="mt-6 text-gray-400 text-sm font-medium hover:text-white transition-colors uppercase tracking-widest"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default GetVerified;
